import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  contractsApi,
  Contract,
  ContractDisplayStatus,
  ContractStatusSummary,
  ContractType,
} from '../../api/contractsApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  shouldShowBuildingSelector,
  getBuildingSelectProps,
  selectBuilding,
} from '../../utils/buildingSelection';
import { formatVndAmount } from '../../utils/vndFormat';
import { formatDateToDisplay } from '../../utils/apartmentDate';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ContractFormModal from './ContractFormModal';
import ContractHistoryTab from './ContractHistoryTab';
import ContractInvoiceListTab from './ContractInvoiceListTab';
import { ManualContractInvoicesModal } from './ManualContractInvoicesModal';

const STATUS_CARD_CONFIG: Record<
  ContractDisplayStatus,
  { label: string; bg: string; border: string; text: string }
> = {
  SIGNED: { label: 'Đã ký', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
  EXPIRING_SOON: { label: 'Sắp hết hạn', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
  EXPIRED: { label: 'Hết hạn', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
  PENDING_SIGNATURE: { label: 'Chờ ký', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
  LIQUIDATED: { label: 'Đã thanh lý', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' },
};

interface ContractListPageProps {
  contractType: ContractType;
}

const REVENUE_LIST_COLSPAN = 13;
const EXPENDITURE_LIST_COLSPAN = 8;
const ACTIONS_MENU_WIDTH_PX = 208;
const ACTIONS_MENU_EST_HEIGHT_PX = 280;

type ContractActionsMenuState = {
  contract: Contract;
  left: number;
  top: number;
  openUpward: boolean;
};

const thClass = 'px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap';
const tdClass = 'px-3 py-3 text-sm text-gray-800 align-top';
const moneyClass = `${tdClass} text-right whitespace-nowrap tabular-nums`;

const ContractListPage: React.FC<ContractListPageProps> = ({ contractType }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRevenue = contractType === 'REVENUE';
  const title = isRevenue ? 'Hợp đồng thu' : 'Hợp đồng chi';

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [summaries, setSummaries] = useState<ContractStatusSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [displayStatusFilter, setDisplayStatusFilter] = useState<ContractDisplayStatus | ''>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [activeTab, setActiveTab] = useState<'list' | 'invoices' | 'history'>('list');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingContractId, setEditingContractId] = useState<string | null>(null);
  const [manualInvoiceContract, setManualInvoiceContract] = useState<Contract | null>(null);
  const [actionsMenu, setActionsMenu] = useState<ContractActionsMenuState | null>(null);

  const closeActionsMenu = useCallback(() => setActionsMenu(null), []);

  const openActionsMenu = useCallback(
    (contract: Contract, event: React.MouseEvent<HTMLButtonElement>) => {
      if (actionsMenu?.contract.id === contract.id) {
        setActionsMenu(null);
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < ACTIONS_MENU_EST_HEIGHT_PX;
      const left = Math.max(
        8,
        Math.min(rect.right - ACTIONS_MENU_WIDTH_PX, window.innerWidth - ACTIONS_MENU_WIDTH_PX - 8),
      );
      const top = openUpward ? rect.top - 8 : rect.bottom + 4;
      setActionsMenu({ contract, left, top, openUpward });
    },
    [actionsMenu?.contract.id],
  );

  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadData();
    }
  }, [selectedBuildingId, search, displayStatusFilter, page]);

  useEffect(() => {
    if (!actionsMenu) return;
    const close = () => closeActionsMenu();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [actionsMenu, closeActionsMenu]);

  const loadBuildings = async () => {
    try {
      const data = await buildingsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch {
      toast.error('Không thể tải danh sách tòa nhà');
    }
  };

  const loadData = async () => {
    if (!selectedBuildingId) return;
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        contractsApi.list({
          buildingId: selectedBuildingId,
          contractType,
          search: search || undefined,
          displayStatus: displayStatusFilter || undefined,
          page,
          limit: 20,
        }),
        contractsApi.getStatusSummary(selectedBuildingId, contractType),
      ]);
      setContracts(listRes.items);
      setTotal(listRes.total);
      setTotalPages(listRes.totalPages);
      setSummaries(summaryRes);
    } catch {
      toast.error('Không thể tải danh sách hợp đồng');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    if (!selectedBuildingId) {
      toast.error('Vui lòng chọn tòa nhà');
      return;
    }
    setEditingContractId(null);
    setFormModalOpen(true);
  };

  const openEditModal = (contractId: string) => {
    setEditingContractId(contractId);
    setFormModalOpen(true);
  };

  const handleDelete = async (contract: Contract) => {
    if (!window.confirm(`Xóa hợp đồng "${contract.name}"?`)) return;
    try {
      await contractsApi.remove(contract.id);
      toast.success('Đã xóa hợp đồng');
      loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Không thể xóa hợp đồng');
    }
  };

  const handleGenerateInvoices = async (contract: Contract) => {
    try {
      const preview = await contractsApi.previewGenerateInvoices(contract.id);
      if (preview.manualSchedule && preview.manualScheduleComplete) {
        if (
          !window.confirm(
            `${preview.warnings.join('\n')}\n\nChạy Tạo HĐ tự động anyway? (sẽ không thay đổi nếu tổng vẫn khớp)`,
          )
        ) {
          return;
        }
      }
      const createCount = preview.periodsToCreate.length * (contract.apartments?.length ?? 1);
      const syncCount = preview.periodsToSync.length * (contract.apartments?.length ?? 1);
      const lines = [
        `Hợp đồng ${preview.contractCode}`,
        `Lịch thu: ${preview.periods.length} kỳ × ${formatVndAmount(preview.amountPerPeriod)} / kỳ`,
        preview.periodsToCreate.length > 0
          ? `Sẽ tạo mới: ${preview.periodsToCreate.length} kỳ (${preview.periodsToCreate.slice(0, 6).join(', ')}${preview.periodsToCreate.length > 6 ? ', …' : ''})`
          : 'Không có kỳ mới (chỉ đồng bộ hóa đơn đã có)',
        preview.periodsToSync.length > 0
          ? `Đồng bộ hóa đơn đã có: ${preview.periodsToSync.length} kỳ`
          : null,
        preview.priorPaidPeriods.length > 0
          ? `Đánh dấu Đã thanh toán (đã thu trước HT): ${preview.priorPaidPeriods.length} kỳ`
          : null,
        ...preview.warnings.map((w) => `⚠ ${w}`),
        '',
        createCount > 0 || syncCount > 0
          ? 'Tiếp tục Tạo HĐ?'
          : 'Hóa đơn đã khớp lịch HĐ. Chạy lại để đồng bộ số tiền / trạng thái?',
      ]
        .filter(Boolean)
        .join('\n');

      if (!window.confirm(lines)) {
        return;
      }

      const result = await contractsApi.generateInvoices(contract.id, { force: true });
      if (result.inSync) {
        toast.success('Hóa đơn đã đồng bộ với hợp đồng (không có thay đổi)');
      } else {
        const parts: string[] = [];
        if (result.created > 0) {
          parts.push(`tạo ${result.created}`);
        }
        if (result.updated && result.updated > 0) {
          parts.push(`cập nhật ${result.updated}`);
        }
        const periods = result.billingPeriods?.length
          ? result.billingPeriods.join(', ')
          : result.billingPeriod ?? '';
        toast.success(`Đã ${parts.join(', ')} hóa đơn (${periods})`);
        if (result.warnings?.length) {
          result.warnings.forEach((w) => toast(w, { icon: '⚠️' }));
        }
      }
      loadData();
    } catch (err: unknown) {
      toast.error(
        getApiErrorMessage(err, 'Không thể tạo hóa đơn'),
      );
    }
  };

  const handleCreateReceipt = (contract: Contract) => {
    if (isRevenue) {
      navigate(`/payment?contractId=${contract.id}&buildingId=${selectedBuildingId}`);
    }
  };

  const handleExport = async () => {
    if (!selectedBuildingId) {
      toast.error('Vui lòng chọn tòa nhà');
      return;
    }
    try {
      setExporting(true);
      await contractsApi.exportContracts(selectedBuildingId, contractType);
      toast.success('Đã xuất dữ liệu hợp đồng');
    } catch {
      toast.error('Không thể xuất dữ liệu');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!selectedBuildingId || !importFile) {
      toast.error('Vui lòng chọn tòa nhà và file import');
      return;
    }
    setImportLoading(true);
    try {
      const result = await contractsApi.importContracts(selectedBuildingId, contractType, importFile);
      toast.success(`Import xong: ${result.created} mới, ${result.updated} cập nhật, ${result.skipped} bỏ qua`);
      setShowImportModal(false);
      setImportFile(null);
      loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Import thất bại');
    } finally {
      setImportLoading(false);
    }
  };

  const getStatusLabel = (status: ContractDisplayStatus) => STATUS_CARD_CONFIG[status]?.label || status;

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isRevenue ? 'Quản lý hợp đồng thu khai thác dịch vụ' : 'Quản lý hợp đồng chi phí vận hành'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {shouldShowBuildingSelector(buildings) && (
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
              disabled={!needsBuildingSelection(buildings)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
            >
              {getBuildingSelectProps(buildings).showPlaceholder && (
                <option value="">-- Chọn tòa nhà --</option>
              )}
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !selectedBuildingId}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {exporting ? 'Đang xuất...' : 'Xuất dữ liệu'}
          </button>
          <button
            type="button"
            onClick={() => {
              setImportFile(null);
              setShowImportModal(true);
            }}
            disabled={!selectedBuildingId}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Nhập dữ liệu
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-md"
          >
            + Tạo mới
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'list'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Danh sách
        </button>
        {isRevenue ? (
          <button
            type="button"
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'invoices'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Danh sách hóa đơn
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'history'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Lịch sử
        </button>
      </div>

      {activeTab === 'invoices' && isRevenue ? (
        <ContractInvoiceListTab buildingId={selectedBuildingId} />
      ) : null}

      {activeTab === 'history' ? (
        <ContractHistoryTab buildingId={selectedBuildingId} contractType={contractType} />
      ) : null}

      {activeTab === 'list' ? (
      <>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {(Object.keys(STATUS_CARD_CONFIG) as ContractDisplayStatus[]).map((status) => {
          const config = STATUS_CARD_CONFIG[status];
          const summary = summaries.find((s) => s.status === status);
          const isActive = displayStatusFilter === status;
          return (
            <button
              key={status}
              type="button"
              onClick={() => {
                setDisplayStatusFilter(isActive ? '' : status);
                setPage(1);
              }}
              className={`text-left p-4 rounded-lg border ${config.bg} ${config.border} ${isActive ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
            >
              <div className={`text-sm font-semibold ${config.text}`}>{config.label}</div>
              <div className="text-2xl font-bold mt-1">{summary?.count ?? 0}</div>
              <div className="text-xs text-gray-600 mt-2 space-y-0.5">
                <div>Giá trị: {formatVndAmount(summary?.totalValue ?? 0)}</div>
                {!isRevenue ? null : (
                  <>
                    <div>Đã thu: {formatVndAmount(summary?.collected ?? 0)}</div>
                    <div>Còn lại: {formatVndAmount(summary?.remaining ?? 0)}</div>
                    <div>HĐ chờ TT: {summary?.pendingInvoices ?? 0}</div>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 mb-4 p-4 space-y-2">
        <input
          type="text"
          placeholder="Tìm mã HĐ, tên, khách hàng..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full md:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
        {isRevenue ? (
          <p className="text-xs text-gray-500">
            <span className="font-medium text-gray-600">Thu phí:</span> Đã thu trước HT = số tiền trên HĐ (trước hệ thống);
            Đã thu theo HĐ = tổng từ hóa đơn/phiếu thu; Kỳ thu cuối (theo HĐ) = kỳ PAID/HT mới nhất; Kỳ HT = marker kỳ đã thu cuối trên HĐ.
          </p>
        ) : null}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto shadow-sm overscroll-x-contain">
        <table className="min-w-[1100px] w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            {isRevenue ? (
              <tr className="border-b border-gray-200">
                <th colSpan={4} className={`${thClass} text-left bg-gray-50`}>Hợp đồng</th>
                <th colSpan={4} className={`${thClass} text-center bg-slate-50 border-x border-gray-200`}>
                  Thu phí
                </th>
                <th colSpan={4} className={`${thClass} text-left bg-gray-50`}>Thời hạn &amp; trạng thái</th>
                <th
                  rowSpan={2}
                  className={`${thClass} text-center sticky right-0 z-[3] bg-gray-50 border-l border-gray-200 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]`}
                >
                  Thao tác
                </th>
              </tr>
            ) : null}
            <tr>
              <th className={`${thClass} text-left min-w-[9rem] sticky left-0 z-[3] bg-gray-50 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]`}>
                Mã / Tên
              </th>
              <th className={`${thClass} text-left max-w-[8rem]`}>Căn hộ</th>
              <th className={`${thClass} text-left min-w-[10rem]`}>
                {isRevenue ? 'Khách hàng' : 'Nhà cung cấp'}
              </th>
              <th className={`${thClass} text-right`}>Giá trị HĐ</th>
              {isRevenue ? (
                <>
                  <th className={`${thClass} text-right bg-slate-50/80 border-l border-gray-200`} title="Số tiền đã thu (trước hệ thống)">
                    Đã thu trước HT
                  </th>
                  <th className={`${thClass} text-right bg-slate-50/80`} title="Đã thu theo hóa đơn">
                    Đã thu theo HĐ
                  </th>
                  <th className={`${thClass} text-right bg-slate-50/80`}>Còn lại</th>
                  <th className={`${thClass} text-center bg-slate-50/80 border-r border-gray-200 min-w-[7rem]`}>
                    <span className="block">Kỳ thu cuối</span>
                    <span className="block font-normal normal-case text-[10px] text-gray-500">(theo HĐ)</span>
                  </th>
                </>
              ) : null}
              <th className={`${thClass} text-left whitespace-nowrap`}>Thời hạn</th>
              <th className={`${thClass} text-left`}>Trạng thái</th>
              <th className={`${thClass} text-left`}>Kỳ TT</th>
              {isRevenue ? (
                <th className={`${thClass} text-center`} title="Kỳ thu phí đã thu cuối (marker HT trên HĐ)">
                  Kỳ HT
                </th>
              ) : null}
              {!isRevenue ? (
                <th
                  className={`${thClass} text-center sticky right-0 bg-gray-50 border-l border-gray-200`}
                >
                  Thao tác
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              <tr>
                <td
                  colSpan={isRevenue ? REVENUE_LIST_COLSPAN : EXPENDITURE_LIST_COLSPAN}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  Đang tải...
                </td>
              </tr>
            ) : contracts.length === 0 ? (
              <tr>
                <td
                  colSpan={isRevenue ? REVENUE_LIST_COLSPAN : EXPENDITURE_LIST_COLSPAN}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  Chưa có hợp đồng
                </td>
              </tr>
            ) : (
              contracts.map((contract) => {
                const statusConfig = STATUS_CARD_CONFIG[contract.displayStatus];
                const priorHt = contract.priorCollectedAmount ?? 0;
                const invoiceCollected = contract.invoiceCollected ?? 0;
                const remaining = contract.remaining ?? 0;
                const menuOpen = actionsMenu?.contract.id === contract.id;
                return (
                  <tr key={contract.id} className="hover:bg-gray-50/80 group">
                    <td className={`${tdClass} sticky left-0 z-[2] bg-white group-hover:bg-gray-50/80 max-w-[14rem] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.06)]`}>
                      <div className="font-semibold text-gray-900 leading-tight">{contract.code}</div>
                      <div className="text-xs text-gray-600 mt-0.5 line-clamp-2" title={contract.name}>
                        {contract.name}
                      </div>
                      {isRevenue && contract.usesManualInvoiceSchedule ? (
                        <span className="inline-block mt-1 text-[10px] font-medium uppercase tracking-wide text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                          HĐ thủ công
                        </span>
                      ) : null}
                    </td>
                    <td className={`${tdClass} text-gray-600 max-w-[8rem]`}>
                      {contract.apartments?.length
                        ? contract.apartments.map((row, index) => (
                            <span key={row.apartment.id}>
                              {index > 0 ? ', ' : null}
                              <Link
                                to={`/apartments/${row.apartment.id}/details`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {row.apartment.code}
                              </Link>
                            </span>
                          ))
                        : contract.apartmentCodes || '—'}
                    </td>
                    <td className={`${tdClass} max-w-[12rem] truncate`} title={contract.customerName || contract.vendorName || ''}>
                      {contract.customerName || contract.vendorName || '—'}
                    </td>
                    <td className={moneyClass}>{formatVndAmount(contract.valueWithVat)}</td>
                    {isRevenue ? (
                      <>
                        <td className={`${moneyClass} bg-slate-50/50 border-l border-gray-100`}>
                          {priorHt > 0 ? formatVndAmount(priorHt) : '—'}
                        </td>
                        <td className={`${moneyClass} bg-slate-50/50`}>
                          {invoiceCollected > 0 ? formatVndAmount(invoiceCollected) : '—'}
                        </td>
                        <td className={`${moneyClass} bg-slate-50/50 ${remaining > 0 ? 'text-amber-800 font-medium' : 'text-gray-600'}`}>
                          {formatVndAmount(remaining)}
                        </td>
                        <td className={`${tdClass} text-center whitespace-nowrap bg-slate-50/50 border-r border-gray-100`}>
                          {contract.lastCollectionPeriod || '—'}
                        </td>
                      </>
                    ) : null}
                    <td className={`${tdClass} whitespace-nowrap text-xs text-gray-700`}>
                      <div>{formatDateToDisplay(contract.startDate)}</div>
                      <div className="text-gray-500">
                        → {contract.endDate ? formatDateToDisplay(contract.endDate) : '—'}
                      </div>
                    </td>
                    <td className={tdClass}>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                      >
                        {getStatusLabel(contract.displayStatus)}
                      </span>
                    </td>
                    <td className={`${tdClass} text-xs text-gray-700 whitespace-nowrap`}>
                      <div>{contract.paymentCycleMonths} tháng</div>
                      {isRevenue ? (
                        <div className="text-gray-500">
                          {contract.collectionTiming === 'POSTPAID' ? 'Thu sau' : 'Thu trước'}
                        </div>
                      ) : null}
                    </td>
                    {isRevenue ? (
                      <td className={`${tdClass} text-center whitespace-nowrap text-xs`}>
                        {contract.lastRecordedBillingPeriod || '—'}
                      </td>
                    ) : null}
                    <td
                      className={`${tdClass} sticky right-0 z-[2] bg-white group-hover:bg-gray-50/80 border-l border-gray-100 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.06)] min-w-[6.5rem]`}
                    >
                      <div className="flex justify-center">
                        <button
                          type="button"
                          className={`px-2 py-1 rounded border text-xs font-medium hover:bg-gray-50 ${
                            menuOpen
                              ? 'border-blue-400 bg-blue-50 text-blue-800'
                              : 'border-gray-300 text-gray-700'
                          }`}
                          onClick={(e) => openActionsMenu(contract, e)}
                          aria-expanded={menuOpen}
                          aria-haspopup="menu"
                        >
                          Thao tác ▾
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {actionsMenu ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[200] cursor-default bg-black/10"
            aria-label="Đóng menu thao tác"
            onClick={closeActionsMenu}
          />
          <div
            role="menu"
            className="fixed z-[201] max-h-[min(70vh,20rem)] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-xl text-left"
            style={{
              width: ACTIONS_MENU_WIDTH_PX,
              left: actionsMenu.left,
              top: actionsMenu.top,
              transform: actionsMenu.openUpward ? 'translateY(-100%)' : undefined,
            }}
          >
            <button
              type="button"
              role="menuitem"
              className="w-full px-3 py-2.5 text-sm text-left hover:bg-gray-50"
              onClick={() => {
                closeActionsMenu();
                openEditModal(actionsMenu.contract.id);
              }}
            >
              Sửa hợp đồng
            </button>
            {isRevenue && actionsMenu.contract.status === 'SIGNED' ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2.5 text-sm text-left text-indigo-700 hover:bg-indigo-50"
                  onClick={() => {
                    closeActionsMenu();
                    setManualInvoiceContract(actionsMenu.contract);
                  }}
                >
                  Tạo HĐ thủ công
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2.5 text-sm text-left text-purple-700 hover:bg-purple-50"
                  onClick={() => {
                    closeActionsMenu();
                    handleGenerateInvoices(actionsMenu.contract);
                  }}
                >
                  Tạo HĐ (tự động)
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2.5 text-sm text-left text-green-700 hover:bg-green-50"
                  onClick={() => {
                    closeActionsMenu();
                    handleCreateReceipt(actionsMenu.contract);
                  }}
                >
                  Tạo phiếu thu
                </button>
              </>
            ) : null}
            {!isRevenue && actionsMenu.contract.status === 'SIGNED' ? (
              <Link
                to={`/contracts/expenditure/${actionsMenu.contract.id}/expenditure-voucher`}
                role="menuitem"
                className="block px-3 py-2.5 text-sm text-green-700 hover:bg-green-50"
                onClick={closeActionsMenu}
              >
                Tạo phiếu chi
              </Link>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className="w-full px-3 py-2.5 text-sm text-left text-red-600 hover:bg-red-50 border-t border-gray-100"
              onClick={() => {
                closeActionsMenu();
                handleDelete(actionsMenu.contract);
              }}
            >
              Xóa
            </button>
          </div>
        </>
      ) : null}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Trước
          </button>
          <span className="text-sm text-gray-600">Trang {page}/{totalPages} — Tổng {total}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Sau
          </button>
        </div>
      )}
      </>
      ) : null}

      <ContractFormModal
        isOpen={formModalOpen}
        contractType={contractType}
        buildingId={selectedBuildingId}
        contractId={editingContractId}
        onClose={() => {
          setFormModalOpen(false);
          setEditingContractId(null);
        }}
        onSaved={loadData}
      />

      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Nhập dữ liệu hợp đồng</h2>
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Chọn file JSON đã xuất từ hệ thống. Hợp đồng trùng mã sẽ được cập nhật.
            </p>
            <input
              type="file"
              accept=".json,application/json"
              onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 border rounded"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={!importFile || importLoading}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
              >
                {importLoading ? 'Đang nhập...' : 'Nhập dữ liệu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {manualInvoiceContract ? (
        <ManualContractInvoicesModal
          contract={manualInvoiceContract}
          open={Boolean(manualInvoiceContract)}
          onClose={() => setManualInvoiceContract(null)}
          onSuccess={() => loadData()}
        />
      ) : null}
    </div>
  );
};

export default ContractListPage;
